export const CMS_LOCATIONS_MAX_PER_PAGE = 300

const readLastPage = (meta: unknown): number => {
	const parsed = meta as { lastPage?: number | null; last_page?: number | null } | null | undefined
	const lastPage = parsed?.lastPage ?? parsed?.last_page
	return typeof lastPage === 'number' && lastPage > 0 ? lastPage : 1
}

export interface ICMSLocationFlagFilters {
	showInFooter?: boolean
	showInMenu?: boolean
	showOnLocations?: boolean
	deliveryBus?: boolean
	passengerBus?: boolean
}

const FLAG_FILTER_PARAM_NAMES: Record<keyof ICMSLocationFlagFilters, string> = {
	showInFooter: 'show_in_footer',
	showInMenu: 'show_in_menu',
	showOnLocations: 'show_on_locations',
	deliveryBus: 'delivery_bus',
	passengerBus: 'passenger_bus',
}

const flagFilterParams = (filters?: ICMSLocationFlagFilters): string[] => {
	if (!filters) return []

	return (Object.keys(FLAG_FILTER_PARAM_NAMES) as (keyof ICMSLocationFlagFilters)[])
		.filter((key) => typeof filters[key] === 'boolean')
		.map((key) => `${FLAG_FILTER_PARAM_NAMES[key]}=${filters[key]}`)
}

const groupPrefix = 'cms-locations'
const relatedPrefix = 'cms-locations-related'

export const apiCMSLocations = {
	fetchCMSLocation: async (locationID: number) => {
		const { data } = await apiCommon<ICMSLocation>(`${groupPrefix}/${locationID}`, { method: 'GET' })
		return CMSLocationFromJSON(data)
	},

	fetchCMSLocationBySlug: async (slug: string, locale: string, isActive: boolean) => {
		let url = `${groupPrefix}/slug/find`

		const queryParams = []

		if (slug) {
			queryParams.push(`slug=${slug}`)
		}

		if (locale) {
			queryParams.push(`locale=${locale}`)
		}

		if (isActive) {
			queryParams.push(`is_active=${isActive}`)
		}

		if (queryParams.length > 0) {
			url += `?${queryParams.join('&')}`
		}

		const { data } = await apiCommon<any>(url, {
			method: 'GET',
		})

		if (!data) return { response: [] }

		const response = CMSLocationFromJSON(data)

		return { data: response }
	},

	fetchAllCMSLocations: async (
		page = 1,
		perPage = 10,
		name?: string,
		assign = false,
		mainCmsLocationId?: number,
		only_active = false,
		lang?: string,
		flagFilters?: ICMSLocationFlagFilters,
		options?: { skipSSRPayload?: boolean },
	) => {
		let url = groupPrefix

		const queryParams = []

		if (perPage && page) {
			queryParams.push(`per_page=${perPage}`)
			queryParams.push(`page=${page}`)
		}

		if (name) {
			queryParams.push(`name=${encodeURIComponent(name)}`)
		}

		if (assign) {
			queryParams.push(`assign=${assign}`)
		}

		if (mainCmsLocationId) {
			queryParams.push(`main_cms_location_id=${mainCmsLocationId}`)
		}

		if (only_active) {
			queryParams.push(`only_active=${only_active}`)
		}

		if (lang) {
			queryParams.push(`lang=${lang}`)
		}

		queryParams.push(...flagFilterParams(flagFilters))

		if (queryParams.length > 0) {
			url += `?${queryParams.join('&')}`
		}

		const { data, links, meta } = await apiCommon<ICMSLocation[]>(url, {
			method: 'GET',
			skipSSRPayload: options?.skipSSRPayload,
		})

		if (!data) return { response: [], links, meta }

		const response = data.map((location) => {
			return CMSLocationFromJSON(location)
		})
		return { data: response, links, meta }
	},

	fetchEveryCMSLocation: async (
		lang?: string,
		options?: { onlyActive?: boolean; flagFilters?: ICMSLocationFlagFilters; mainCmsLocationId?: number; skipSSRPayload?: boolean },
	) => {
		const onlyActive = !!options?.onlyActive && !!lang

		const fetchPage = (page: number) =>
			apiCMSLocations.fetchAllCMSLocations(
				page,
				CMS_LOCATIONS_MAX_PER_PAGE,
				'',
				false,
				options?.mainCmsLocationId,
				onlyActive,
				lang,
				options?.flagFilters,
				{ skipSSRPayload: options?.skipSSRPayload },
			)

		const firstPage = await fetchPage(1)
		const lastPage = readLastPage(firstPage?.meta)

		const collected: ICMSLocationModel[][] = [firstPage?.data ?? []]

		if (lastPage > 1) {
			const restPages = await Promise.all(Array.from({ length: lastPage - 1 }, (_, index) => fetchPage(index + 2)))
			restPages.forEach((page) => collected.push(page?.data ?? []))
		}

		return { data: collected.flat() }
	},

	updateCMSLocation: async (locationData: ICMSLocationModel, locationID: number) => {
		const { data } = await apiCommon<ICMSLocation>(`${groupPrefix}/${locationID}`, { method: 'PUT', body: CMSLocationToJSON(locationData) })
		return CMSLocationFromJSON(data)
	},

	createCMSLocation: async (locationData: ICMSLocationModel) => {
		const { data } = await apiCommon<ICMSLocation>(`${groupPrefix}`, {
			method: 'POST',
			body: CMSLocationToJSON(locationData),
		})
		return CMSLocationFromJSON(data)
	},

	deleteCMSLocation: async (locationID: number) => {
		const data = await apiCommon<string>(`${groupPrefix}/${locationID}`, { method: 'DELETE' })
		return data
	},

	fetchAllRelatedLocations: async () => {
		const { data } = await apiCommon<ICMSLocationRelatedExtended[]>(`${relatedPrefix}`, { method: 'GET' })
		if (!data) return { response: [] }

		const response = data.map((location) => {
			return CMSLocationRelatedExtendedFromJSON(location)
		})

		return { data: response }
	},

	fetchAssignedLocations: async (locationID: number) => {
		const { data } = await apiCommon<ICMSLocationRelated[]>(`${relatedPrefix}/${locationID}`, { method: 'GET' })
		if (!data) return { response: [] }

		const response = data.map((location) => {
			return CMSLocationRelatedFromJSON(location)
		})

		return { data: response }
	},

	assignRelatedLocations: async (locationsToAdd: number[], locationsToRemove: number[], locationID: number) => {
		const data = await apiCommon<any>(`${relatedPrefix}/${locationID}`, {
			method: 'PUT',
			body: {
				locations_to_add: locationsToAdd,
				locations_to_remove: locationsToRemove,
			},
		})
		return data
	},

	fetchClosestLocations: async (locationID: number, excludeIds?: number[]) => {
		let url = `${groupPrefix}/${locationID}/closest`

		if (excludeIds && excludeIds.length > 0) {
			const queryParams = excludeIds.map((excludeId) => `exclude_ids[]=${excludeId}`)
			url += `?${queryParams.join('&')}`
		}

		const { data } = await apiCommon<ICMSLocation[]>(url, { method: 'GET' })
		if (!data) return { response: [] }

		const response = data.map((location) => {
			return CMSLocationFromJSON(location)
		})

		return { data: response }
	},
}
